'use client';
/* eslint-disable react-perf/jsx-no-new-function-as-prop */

import * as React from 'react';
import useEmblaCarousel, {type UseEmblaCarouselType} from 'embla-carousel-react';

import {cn} from '../../shared/tailwind';

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
	opts?: CarouselOptions;
	plugins?: CarouselPlugin;
	orientation?: 'horizontal' | 'vertical';
	setApi?: (api: CarouselApi) => void;
};

type CarouselContextProps = {
	carouselRef: ReturnType<typeof useEmblaCarousel>[0];
	api: ReturnType<typeof useEmblaCarousel>[1];
	scrollPrev: () => void;
	scrollNext: () => void;
	canScrollPrev: boolean;
	canScrollNext: boolean;
	selectedIndex: number;
	setSelectedIndex: React.Dispatch<React.SetStateAction<number>>;
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

function useCarousel() {
	const context = React.useContext(CarouselContext);

	if (!context) {
		throw new Error('useCarousel must be used within a <Carousel />');
	}

	return context;
}

const Carousel = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement> & CarouselProps
>(({orientation = 'horizontal', opts, setApi, plugins, className, children, ...props}, ref) => {
	const [carouselRef, api] = useEmblaCarousel(
		{
			...opts,
			axis: orientation === 'horizontal' ? 'x' : 'y',
		},
		plugins,
	);
	const [canScrollPrev, setCanScrollPrev] = React.useState(false);
	const [canScrollNext, setCanScrollNext] = React.useState(false);
	const [selectedIndex, setSelectedIndex] = React.useState(0);

	const onSelect = React.useCallback((api: CarouselApi) => {
		if (!api) {
			return;
		}
		setCanScrollPrev(api.canScrollPrev());
		setCanScrollNext(api.canScrollNext());
		setSelectedIndex(api.selectedScrollSnap());
	}, []);

	const scrollPrev = React.useCallback(() => {
		api?.scrollPrev();
	}, [api]);

	const scrollNext = React.useCallback(() => {
		api?.scrollNext();
	}, [api]);

	React.useEffect(() => {
		if (!api || !setApi) {
			return;
		}
		setApi(api);
	}, [api, setApi]);

	React.useEffect(() => {
		if (!api) {
			return;
		}
		onSelect(api);
		api.on('reInit', onSelect);
		api.on('select', onSelect);

		return () => {
			api?.off('select', onSelect);
		};
	}, [api, onSelect]);

	return (
		<CarouselContext.Provider
			value={{
				carouselRef,
				api,
				opts,
				orientation,
				scrollPrev,
				scrollNext,
				canScrollPrev,
				canScrollNext,
				selectedIndex,
				setSelectedIndex,
			}}
		>
			<div
				ref={ref}
				className={cn('relative', className)}
				role="region"
				aria-roledescription="carousel"
				{...props}
			>
				{children}
				<CarouselIndicators />
			</div>
		</CarouselContext.Provider>
	);
});
Carousel.displayName = 'Carousel';

const CarouselIndicators = () => {
	const {api, selectedIndex} = useCarousel();
	const slideCount = api?.scrollSnapList().length || 0;

	return (
		<div className="mt-4 flex justify-center gap-2">
			{Array.from({length: slideCount}).map((_, index) => (
				<button
					key={index}
					className={cn(
						'h-1 w-8 rounded-[1px] transition-colors',
						index === selectedIndex ? 'bg-primary' : 'bg-[#CAC7C6]',
					)}
					type="button"
					onClick={() => api?.scrollTo(index)}
				/>
			))}
		</div>
	);
};

const CarouselContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
	({className, ...props}, ref) => {
		const {carouselRef, orientation} = useCarousel();
		return (
			<div ref={carouselRef} className="overflow-hidden">
				<div
					ref={ref}
					className={cn(
						'flex',
						orientation === 'horizontal' ? '-ml-4' : '-mt-4 flex-col',
						className,
					)}
					{...props}
				/>
			</div>
		);
	},
);
CarouselContent.displayName = 'CarouselContent';

const CarouselItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
	({className, ...props}, ref) => {
		const {orientation} = useCarousel();
		return (
			<div
				ref={ref}
				role="group"
				aria-roledescription="slide"
				className={cn(
					'flex-none w-[260px]',
					orientation === 'horizontal' ? 'pl-5' : 'pt-5',
					className,
				)}
				{...props}
			/>
		);
	},
);
CarouselItem.displayName = 'CarouselItem';

export {Carousel, type CarouselApi, CarouselContent, CarouselItem};
